import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/**
 * Explains what's collected and what deliberately isn't, in the Card layout
 * the other how-to tabs use (cash_flow, commissions, planning).
 *
 * The privacy card is the point: this page reports on colleagues, and anyone
 * looking at it should be able to see exactly where the line was drawn.
 */
export function HowToTab() {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>What this measures</CardTitle>
          <CardDescription>
            One event per feature opened, derived automatically from the sidebar
            registry.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Every time someone opens a feature, the app sends one{" "}
            <code className="rounded bg-muted px-1">feature_viewed</code> event
            carrying the feature&apos;s key, its nav group, and a stripped-down path.
            Because it&apos;s derived from the registry rather than hand-placed, every
            feature is covered and a feature added later is covered the day it&apos;s
            registered — there is no per-page instrumentation to remember.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            These numbers are <strong>production only</strong>. Local and dev share the
            same PostHog project, so every event is tagged with the environment it came
            from and anything that isn&apos;t production is excluded here — otherwise a
            developer clicking around would count as real usage.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>What is deliberately not collected</CardTitle>
          <CardDescription>
            This app renders the P&amp;L, contract values, and customer names, so the
            defaults are inverted from a typical PostHog install.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            <li>
              <strong className="text-foreground">No session recording.</strong>{" "}
              Recordings would be video of exactly that.
            </li>
            <li>
              <strong className="text-foreground">No autocapture.</strong> PostHog&apos;s
              default click-capture reads the text of whatever you clicked — here that
              means account names and dollar figures.
            </li>
            <li>
              <strong className="text-foreground">No record identifiers in URLs.</strong>{" "}
              Account UUIDs and numeric ids become{" "}
              <code className="rounded bg-muted px-1">:id</code>, close periods become{" "}
              <code className="rounded bg-muted px-1">:period</code>, and every query
              value is redacted unless it&apos;s low-cardinality UI state like{" "}
              <code className="rounded bg-muted px-1">tab</code>. Browsing{" "}
              <code className="rounded bg-muted px-1">/contracts/9f1c…?account=Acme</code>{" "}
              is recorded as{" "}
              <code className="rounded bg-muted px-1">
                /contracts/:id?account=:redacted
              </code>
              .
            </li>
            <li>
              <strong className="text-foreground">
                No heatmaps, performance capture, or surveys.
              </strong>{" "}
              These are on by default in PostHog&apos;s server-side config and are pinned
              off in the client, so a toggle in the PostHog UI can&apos;t switch them on
              here.
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How people are identified</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Events are tied to your cinch_ops user id. PostHog never receives names —
            they&apos;re joined back in from our own users table when this page renders,
            which is why someone who has never signed in simply doesn&apos;t appear.
            Events leave the browser through this app&apos;s own{" "}
            <code className="rounded bg-muted px-1">/ph/*</code> path rather than
            posthog.com directly, so ad-blockers don&apos;t silently drop a slice of
            them.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Reading the Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            <li>
              <strong className="text-foreground">vs prior</strong> compares the window
              against the one immediately before it — 7 days against the previous 7.
            </li>
            <li>
              <strong className="text-foreground">Never opened</strong> means no opens in
              this window <em>or</em> the previous one. A feature used last week but not
              this week shows as a drop instead, because those need different responses.
            </li>
            <li>
              <strong className="text-foreground">Retired</strong> marks a key PostHog
              still reports that the app no longer has — almost always a rename. Kept
              visible so historical usage doesn&apos;t silently vanish.
            </li>
            <li>
              Numbers are cached for an hour;{" "}
              <strong className="text-foreground">Refresh</strong> forces a live pull.
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Why goals matter</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Usage on its own can&apos;t tell you whether a number is good. Four opens a
            week is healthy for a monthly close tool and alarming for a daily dashboard.
            The Goals tab records what each feature was <em>for</em> and who should be
            using it, which is what lets the weekly review say &ldquo;this was built so
            CSMs could catch renewals early, and no CSM has opened it in three
            weeks&rdquo; rather than just &ldquo;usage is down 12%&rdquo;.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>A caution about small numbers</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            There are about a dozen people using this app. One person on holiday can look
            like a usage collapse, and a single afternoon of clicking around can look
            like a surge. Treat week-on-week swings as prompts to ask a question, not as
            findings.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
